<template>
  <div>
    <banner-component
        title="Logiciel de gestion pour TPE : devis, factures, achats et export FEC"
        :lead="lead"
        :breadcrumbs="breadcrumbs"
        ruled>
      <a class="btn btn--primary" :href="site.productUrl" rel="noopener">Accéder au gestionnaire</a>
      <router-link class="btn btn--ghost" to="/contact">Poser une question</router-link>
    </banner-component>

    <section class="band screens" aria-label="Aperçu de l'application">
      <div class="container screens-grid">
        <figure class="main">
          <img src="/img/screen_app/statistiques-synthese.webp" width="1600" height="593"
               alt="Tableau de bord Statistiques du logiciel : chiffre d'affaires facturé et encaissé, reste à encaisser, factures en retard, clients actifs, taux de signature et graphiques par période">
          <figcaption class="muted">Le tableau de bord Statistiques : facturé, encaissé, retards et signatures.</figcaption>
        </figure>
        <figure>
          <img src="/img/screen_app/dossier-achats.webp" width="1528" height="625" loading="lazy"
               alt="Vue d'un dossier : vendu, acheté, marge et reste à payer, contrats avec devis, acompte et facture, achats rattachés">
          <figcaption class="muted">Un dossier : ce qui est vendu, acheté et ce qu'il reste en marge.</figcaption>
        </figure>
        <figure>
          <img src="/img/screen_app/export-comptable.webp" width="1600" height="722" loading="lazy"
               alt="Export comptable : écritures, totaux débit et crédit, contrôles de la période et boutons d'export FEC et Excel">
          <figcaption class="muted">L'export comptable : FEC, Excel, contrôles et lettrage.</figcaption>
        </figure>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="functions-title">
      <div class="container">
        <h2 id="functions-title">Les fonctions du logiciel de gestion</h2>
        <p class="lead">Sept domaines qui s'enchaînent sur un même dossier client, du premier contact à la comptabilité.</p>
        <ul class="function-list">
          <li v-for="feature in features" :key="feature.slug">
            <h3><router-link :to="feature.path">{{ feature.name }}</router-link></h3>
            <p>{{ feature.summary }}</p>
          </li>
        </ul>
      </div>
    </section>

    <section class="band" aria-labelledby="team-title">
      <div class="container">
        <h2 id="team-title">Pensé pour travailler à plusieurs, en sécurité</h2>
        <div class="columns">
          <div>
            <h3>Collaboration</h3>
            <ul>
              <li>Espace entreprise partagé</li>
              <li>Travail collaboratif en temps réel</li>
              <li>100 % cloud, disponible à toute heure</li>
              <li>Accès depuis tout appareil</li>
            </ul>
          </div>
          <div>
            <h3>Sécurité et confidentialité</h3>
            <ul>
              <li>Droits selon le rôle de chaque utilisateur</li>
              <li>Chiffrement des données, de l'envoi au stockage</li>
              <li>Accès limité aux seules données utiles pour les clients et les comptables</li>
            </ul>
          </div>
          <div>
            <h3>Suivi et support</h3>
            <ul>
              <li>Indicateurs clairs sur l'activité</li>
              <li>Support réactif</li>
              <li><router-link to="/contact">Une question ? Écrivez-nous</router-link></li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="guides-title">
      <div class="container">
        <div class="section-head">
          <h2 id="guides-title">Avant de choisir, les guides</h2>
          <router-link to="/blog">Tous les guides</router-link>
        </div>
        <article-list :articles="guides"/>
      </div>
    </section>

    <section class="band" aria-labelledby="faq-title">
      <div class="container container--narrow">
        <h2 id="faq-title">Questions fréquentes sur le logiciel</h2>
        <faq-section :items="faq"/>
      </div>
    </section>

    <call-to-action-component/>
  </div>
</template>

<script>
  import site from "@/content/site";
  import features from "@/content/features";
  import articles from "@/content/articles";
  import pages from "@/content/pages";
  import {inline} from "@/seo/inline";
  import {useSeo} from "@/seo/useSeo";
  import {organization, software, webPage, breadcrumb, faqPage} from "@/seo/schema";
  import BannerComponent from "@/components/BannerComponent.vue";
  import ArticleList from "@/components/ArticleList.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  const PATH = '/gestionnaire-citron';

  const BREADCRUMBS = [
    {name: 'Accueil', path: '/'},
    {name: 'Logiciel de gestion TPE', path: PATH}
  ];

  const FAQ = [
    {
      q: 'Qu\'est-ce que le Gestionnaire Citron ?',
      a: 'Une application web de gestion pour les TPE et les indépendants : prospects et clients, devis, factures d\'acompte, factures finales, avoirs, encaissements, achats et stock, export comptable (FEC et Excel) et statistiques, avec facturation électronique au format Factur-X.'
    },
    {
      q: 'Est-ce un CRM, un ERP ou un logiciel de facturation ?',
      a: 'C\'est un logiciel de gestion commerciale qui réunit un CRM simple (prospects et clients), la facturation (devis, acomptes, factures, avoirs) et des fonctions d\'ERP léger (achats, stock, marge, export comptable). Pour choisir, lisez [ERP, CRM ou logiciel de facturation](/blog/erp-crm-logiciel-facturation-tpe).'
    },
    {
      q: 'Faut-il installer un logiciel ?',
      a: 'Non. C\'est une application web : vous y accédez depuis un navigateur, sur ordinateur, tablette ou téléphone.'
    },
    {
      q: 'Est-il prêt pour la facturation électronique de 2026-2027 ?',
      a: 'Il produit des factures au format Factur-X et les transmet via une plateforme agréée partenaire. Pour les dates et les obligations, lisez le [guide sur le calendrier de la réforme](/blog/facturation-electronique-2026-2027-calendrier-obligations).'
    },
    {
      q: 'Le logiciel produit-il un FEC pour mon comptable ?',
      a: 'Oui. L\'[export comptable](/gestionnaire-citron/export-comptable) génère le fichier des écritures comptables (FEC) et un classeur Excel, avec contrôles, lettrage et récapitulatif de TVA.'
    },
    {
      q: 'Gère-t-il les achats, les fournisseurs et le stock ?',
      a: 'Oui. Vous enregistrez les factures et avoirs fournisseurs, leurs paiements, la réception des marchandises et le stock, puis vous ventilez les achats sur vos dossiers pour connaître la marge. Voir [achats et logistique](/gestionnaire-citron/achats-logistique).'
    },
    {
      q: 'Mes données sont-elles protégées ?',
      a: 'Les données sont chiffrées de l\'envoi au stockage. Chaque utilisateur a des droits selon son rôle, et les clients comme les comptables n\'accèdent qu\'aux données utiles.'
    },
    {
      q: 'Qui développe ce logiciel ?',
      a: 'Il est développé à Toulouse par [Franck Besson](/portfolio), fondateur de Citron Digital. Pour une question ou un besoin particulier, [écrivez-nous](/contact).'
    }
  ];

  export default {
    name: 'LemonManagerView',
    components: {BannerComponent, ArticleList, FaqSection, CallToActionComponent},
    setup() {
      const page = pages.find(entry => entry.path === PATH);
      useSeo({
        path: PATH,
        image: '/img/og/gestionnaire-citron.jpg',
        graph: [
          organization(),
          software(),
          webPage(PATH, page.title, page.description, {about: {'@id': software()['@id']}, breadcrumb: {'@id': `${site.url}${PATH}#breadcrumb`}}),
          {...breadcrumb(BREADCRUMBS), '@id': `${site.url}${PATH}#breadcrumb`},
          faqPage(FAQ)
        ]
      });
    },
    data: function () {
      return {
        site,
        features,
        faq: FAQ,
        breadcrumbs: BREADCRUMBS,
        lead: inline('Le **Gestionnaire Citron** est un logiciel de gestion en ligne pour TPE, à mi-chemin entre un **CRM** et un **ERP léger** : il suit vos prospects du premier contact à la signature, établit vos devis et vos **factures électroniques**, gère vos **achats et votre stock**, pilote vos encaissements, prépare l\'**export comptable** et suit vos **statistiques**. Il se partage en équipe et protège vos données.'),
        guides: ['facturation-electronique-2026-2027-calendrier-obligations', 'choisir-logiciel-facturation-tpe-independant', 'facture-acompte-facture-finale']
            .map(slug => articles.find(article => article.slug === slug)),
      }
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.screens
  padding-top clamp(32px, 5vw, 56px)
  padding-bottom clamp(32px, 5vw, 56px)

.screens-grid
  display grid
  gap 28px
  figure
    margin 0
  img
    width 100%
    height auto
    border 2px solid INK
    border-radius 4px
    background WHITE
  figcaption
    margin-top 10px
    font-size 0.9rem

.function-list
  list-style none
  margin 36px 0 0
  padding 0
  display grid
  gap 0 56px
  border-top 2px solid INK
  li
    margin 0
    padding 22px 0
    border-bottom 1px solid RULE
  h3
    margin 0 0 6px
    font-size 1.4rem
    a
      text-decoration none
      &:hover
        text-decoration underline
  p
    margin 0
    color INK_SOFT
    max-width 46ch

.columns
  display grid
  gap 32px
  margin-top 32px
  h3
    margin 0 0 10px
    font-size 1.3rem
    padding-top 14px
    border-top 2px solid INK
  ul
    margin 0
    padding-left 1.2em
  li
    margin-top 6px
    color INK_SOFT
    &::marker
      color LEMON_DEEP

.section-head
  display flex
  flex-wrap wrap
  align-items baseline
  justify-content space-between
  gap 8px 24px
  margin-bottom 28px
  h2
    margin 0

@media (min-width: DESKTOP_SIZE)
  .screens-grid
    grid-template-columns 1fr 1fr
    align-items start
    .main
      grid-column 1 / -1
  .function-list
    grid-template-columns 1fr 1fr
  .columns
    grid-template-columns repeat(3, 1fr)
</style>
